import React from 'react';
import { createValidationErrors, ValidationProps } from 'ui';
import { equalsIgnoringWhitespace } from 'app/infrastructure/data/string';
import { TextConfigurationTranslation } from '../../../constants';
import { getModifiedItem } from '../selectors';
import { getComponentValidation } from '../validation';
import { ModifiedCodeDescription, CodeDescriptionSearchData, CodeDescriptionMetadata } from '../types-store';
import { CodeListHolder, CodeListItem } from './types';
import { getCodeDataId } from './get-code-data-id';

interface CodeDescriptionItemsProps {
  HolderComponent: CodeListHolder;
  ItemComponent: CodeListItem;

  metadata: CodeDescriptionMetadata;
  textsList: CodeDescriptionSearchData[];
  modifiedTexts: ModifiedCodeDescription[];
  modifyText: (modifiedText: ModifiedCodeDescription) => void;
  validation: { [key: string]: ValidationProps };
  translation: TextConfigurationTranslation;
  children?: React.ReactNode;
}

export const CodeDescriptionItems: React.FC<CodeDescriptionItemsProps> = ({
  HolderComponent, ItemComponent,
  metadata, textsList, modifiedTexts, modifyText, validation, translation
}) => {

  return textsList && textsList.length > 0
    ? <HolderComponent translation={translation}>
      {textsList.map((textData, index) => {
        const displayLongDescription = metadata?.codetablesWithLongDesc?.includes(textData?.codetable?.v) ?? false;
        const previousText = index > 0 ? textsList[index - 1] : undefined;
        const modifiedItem = getModifiedItem(modifiedTexts, textData);
        const onModifyText = (modifiedDescription: string, modifiedLongDescription: string) => {
          modifyText({
            id: textData?.id,
            codetable: textData?.codetable?.v ?? undefined,
            language: textData?.language?.v ?? undefined,
            description: modifiedDescription,
            longDescription: modifiedLongDescription,
            onlyWhitespace: equalsIgnoringWhitespace(textData?.description, modifiedDescription) &&
              equalsIgnoringWhitespace(textData?.longDescription, modifiedLongDescription)
          });
        };

        const description = (modifiedItem ? modifiedItem.description : textData?.description) || '';
        const longDescription = (modifiedItem ? modifiedItem.longDescription : textData?.longDescription) || '';

        const modifyDescription = (modifiedDescription: string) => onModifyText(modifiedDescription, longDescription);
        const modifyLongDescription = (modifiedLongDescription: string) => onModifyText(description, modifiedLongDescription);

        const componentValidation = getComponentValidation(validation, textData);
        const finalValidation: { [key: string]: ValidationProps } = {
          ...componentValidation,
          ...(modifiedItem?.description?.trim() === ''
            ? { description: createValidationErrors([translation.must_be_filled_out]) }
            : {})
        };

        return <ItemComponent
          key={getCodeDataId(textData)}
          codeData={textData}
          previousCodeData={previousText}
          description={description}
          longDescription={longDescription}
          modifyDescription={modifyDescription}
          modifyLongDescription={modifyLongDescription}
          edited={!!modifiedItem && !modifiedItem.onlyWhitespace}
          displayLongDescription={displayLongDescription}
          descValidationMessages={finalValidation.description?.messages?.map(message => message.message)}
          longDescValidationMessages={finalValidation.longDescription?.messages?.map(message => message.message)}
          translation={translation}
        />;
      })}
    </HolderComponent>
    : null;
};
